import React, { useEffect, useState } from 'react';

import {
  LoaderSpinner,
} from '@vw-marketing/us-components';
import { FeatureAppLoader } from '@feature-hub/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useFeatureAppConfig } from '../../../shared/hooks/use-feature-app-config';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useImageValidator } from '../../../shared/hooks/use-image-validator';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useUser } from '../../../app/providers/UserContext';
import { useVehicle } from '../../../app/providers/VehicleContext';
import { VehicleSectionFooter } from '../components/vehicle-section-footer/VehicleSectionFooter';
import NoVehicleInteractiveCard from '../components/no-vehicle-interactive-card/NoVehicleInteractiveCard';
import SubtitleAndCTA from '../components/subtitle-and-cta';
import TabsContent from '../../../../src/shared/components/tabs-content';
import VehicleImage from '../components/vehicle-image';
import VehicleInteractiveCard from '../components/vehicle-interactive-card';
import VehicleSection from '../components/vehicle-section/VehicleSection';
// import { useFeatureAppEnvironment } from '../../../shared/hooks/use-feature-app-environment';

export const useVehicleManagement = (
  onOpenAddVehicleLayer?: () => void,
  onOpenGarageLayer?: () => void,
  onOpenNicknameLayer?: () => void,
  onOpenRemoveVehicleLayer?: () => void,
) => {
  const [activeCTA, setActiveCTA] = useState(false);

  const { myVwFaRolloutVehicleManagement } = useFlags();
  const { 'featureapp-service': featureAppService } = useFeatureServices();
  // const { faServicesUrl = '' } = useFeatureAppConfig();
  const { translationHandler } = useMyVwContext();
  const { vehicleManagementFAName = '' } = useFeatureAppConfig();
  const { user } = useUser();
  const { vehicles = [] } = user || {};
  const faConfiguration = featureAppService?.featureAppConfigurations[vehicleManagementFAName];
  const isSwitchCtaAvailable: boolean = vehicles.length > 1;

  const {
    baseUrl,
    config = {},
    src,
    name: featureAppName,
    serverSrc,
  } = (faConfiguration || {}) as any;

  // Uncomment this lines for local development
  // const src: string = 'featureApp.js';
  // const baseUrl: string = 'https://qa.vwmyvwvehiclemanagementfeatureapp.svc.vwusa.io';
  // const { config } = useFeatureAppEnvironment();

  const { isLoadingPreferredVehicle, userPreferredVehicle } = useVehicle();

  useEffect(() => {
    if (userPreferredVehicle) setActiveCTA(true);
    else setActiveCTA(false);
  }, [userPreferredVehicle]);

  const handleAction = () => {
    onOpenAddVehicleLayer?.();
  };

  const handleSwitchVehicles = () => {
    onOpenGarageLayer?.();
  }

  const interactiveCard = userPreferredVehicle && !isLoadingPreferredVehicle ? (
    <VehicleInteractiveCard
      onOpenNickname={() => onOpenNicknameLayer?.()}
      onOpenRemoveVehicle={() => onOpenRemoveVehicleLayer?.()}
    />
  ) : (
    <NoVehicleInteractiveCard
      handleClick={() => handleAction()}
    />
  );

  const oldFaItems = [
    {
      key: 'select-vehicle',
      labelKey: 'vehicleManagement.selectVehicle',
      content: (
        <FeatureAppLoader
          baseUrl={baseUrl}
          config={{ ...config, currentScreen: 'select-vehicle' }}
          featureAppId="mmyvw-account-select-vehicle"
          featureAppName={featureAppName}
          serverSrc={serverSrc}
          src={src}
        />
      ),
    },
    {
      key: 'vehicle-information',
      labelKey: 'vehicleManagement.vehicleInformation',
      content: (
        <FeatureAppLoader
          baseUrl={baseUrl}
          config={{ ...config, currentScreen: 'vehicle-information' }}
          featureAppId="mmyvw-account-vehicle-information"
          featureAppName={featureAppName}
          serverSrc={serverSrc}
          src={src}
        />
      ),
    },
    {
      key: 'manage-dealer',
      labelKey: 'vehicleManagement.manageDealer',
      content: (
        <FeatureAppLoader
          baseUrl={baseUrl}
          config={{ ...config, currentScreen: 'manage-dealer' }}
          featureAppId="mmyvw-account-manage-dealer"
          featureAppName={featureAppName}
          serverSrc={serverSrc}
          src={src}
        />
      ),
    },
  ];

  const { imgUrl } = useImageValidator({
    imageUrls: [
      userPreferredVehicle?.representativeImgURLComplete,
      userPreferredVehicle?.representativeImgURLPartial,
    ],
    dependencies: [userPreferredVehicle]
  });

  const cardsOrTabItem = myVwFaRolloutVehicleManagement ? (
    <>
      <SubtitleAndCTA
        subtitle={translationHandler('vehicleManagement.subtitle')}
      />
      {isLoadingPreferredVehicle && !userPreferredVehicle ? (
        <LoaderSpinner />
      ) : (
        <VehicleSection
          activeCTA={activeCTA}
          isSwitchCtaAvailable={isSwitchCtaAvailable}
        >
          <VehicleImage vehicleImageURL={imgUrl} />
          {interactiveCard}
        </VehicleSection>
      )}
      <VehicleSectionFooter
        activeCTA={activeCTA}
        handleClick={() => handleAction()}
        handleSwitchVehicles={handleSwitchVehicles}
      />
    </>
  ) : (
    <TabsContent items={[...oldFaItems]} />
  );

  return { cardsOrTabItem };
};
